import Big from "big.js";
import { Button, DataTableV2, Highlight, Modal, Pill, Text } from "components/commons";
import useDataTable from "hooks/useDataTable";
import { useModal } from "hooks";
import { initialFilterState } from "modules/shift-records/shift-records-filter.state";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { prettifyPaymentMethod } from "utils/pretty.utils";
import { formatAmount } from "utils";
import {
  isAccountabilityEnabled,
  isAccountabilityPaymentMethod,
} from "data/constant/accountability";
import { nonCashTransactionColumn } from "../../shift-records-details-cash-non-cash-column";
import { nonCashTransactionMapper } from "../../shift-records-details-cash-non-cash.mapper";
import locale from "localization";
import initialState from "./payment-method-transaction-modal.state";
import PaymentMethodTransactionFilter from "./payment-method-transaction-filter";
import styles from "./payment-method-transaction-modal.module.scss";
import { FormMode, PaymentMethod } from "enums";
import ReminderHexIcon from "../reminder-hex-icon";
import AdjustPaymentMethodDrawer from "./adjust-payment-method-drawer";
import TagAccountabilityDrawer from "./tag-accountability-drawer";

export default function PaymentMethodTransactionModal({
  paymentMethodTransactions,
  method,
  allTransactions = [],
  transactionsItemDetails = [],
  nonCashTransactions = [],
  onRefetch,
  pageMode,
  updatePaymentMethodRefNumber,
  updateTransactionPaymentMethod,
  updateTransactionAccountability,
  employees = [],
  ...modal
}) {
  const [pageState, setPageState] = useState({});
  const [searchKey, setSearchKey] = useState("");
  const [searchType, setSearchType] = useState("transactionNo");
  const [updatedRefNumbers, setUpdatedRefNumbers] = useState({});
  const [editingRefId, setEditingRefId] = useState(null);

  const isView = pageMode === FormMode.View;

  const adjustPaymentMethodDrawer = useModal();
  const [adjustedTransaction, setAdjustedTransaction] = useState(null);
  const [adjustedRowIndex, setAdjustedRowIndex] = useState(null);

  const onAdjustPaymentMethod = useCallback(
    (transaction, rowIndex) => {
      setAdjustedTransaction(transaction);
      setAdjustedRowIndex(rowIndex);
      adjustPaymentMethodDrawer.show({});
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const tagAccountabilityDrawer = useModal();
  const [accountabilityTransaction, setAccountabilityTransaction] = useState(null);

  const onTagAccountability = useCallback(
    (transaction, rowIndex) => {
      setAccountabilityTransaction(transaction);
      setAdjustedRowIndex(rowIndex);
      tagAccountabilityDrawer.show({});
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const isAccountabilityView = isAccountabilityEnabled && isAccountabilityPaymentMethod(method);

  const columns = useMemo(
    () => [
      ...nonCashTransactionColumn.map((column) =>
        column.key === "paymentMethod" ? { ...column, sticky: "left" } : column,
      ),
      ...(isAccountabilityView
        ? [
            { key: "accountablePerson", text: locale.accountablePerson },
            { key: "amountShouldered", text: locale.amountShouldered },
            { key: "accountabilityStatus", text: locale.status },
          ]
        : []),
      ...(isView ? [] : [{ key: "actions", text: locale.action, sticky: "right" }]),
    ],
    [isView, isAccountabilityView],
  );

  const getAccountableAmount = useCallback(
    (row) => {
      if (row.accountableAmount !== undefined && row.accountableAmount !== null) {
        return row.accountableAmount;
      }
      return Number(
        paymentMethodTransactions
          .filter((t) => t.transactionPaymentMethodId === row.transactionPaymentMethodId)
          .reduce((sum, t) => sum.add(t.finalAmount || 0), Big(0))
          .round(2),
      );
    },
    [paymentMethodTransactions],
  );

  const renderAccountabilityCells = (val, index) => {
    const accountability = val.accountability || [];
    const isTagged = accountability.length > 0 && !val.actionNeeded;
    const openDrawer = () =>
      onTagAccountability(
        {
          transactionPaymentMethodId: val.transactionPaymentMethodId,
          transactionNumber: val.transactionNumber,
          accountability,
          finalAmount: getAccountableAmount(val),
        },
        index,
      );
    const canTag = !isView && Boolean(val.transactionPaymentMethodId);

    return {
      accountablePerson: isTagged ? (
        <Button link disabled={!canTag} onClick={openDrawer} className={styles.assigneeButton}>
          <div className={styles.cellList}>
            {accountability.map((entry, i) => (
              <span key={i}>{entry.name}</span>
            ))}
          </div>
        </Button>
      ) : canTag ? (
        <Button outline onClick={openDrawer} className={styles.tagAccountableButton}>
          {locale.tagAccountable}
        </Button>
      ) : (
        <Text>--</Text>
      ),
      amountShouldered: isTagged ? (
        <div className={styles.cellList}>
          {accountability.map((entry, i) => (
            <Text key={i}>{formatAmount(entry.amount)}</Text>
          ))}
        </div>
      ) : null,
      accountabilityStatus: isTagged ? (
        <Pill grass>{locale.balance}</Pill>
      ) : (
        <Pill cheddar>{locale.untagged}</Pill>
      ),
    };
  };

  const shiftId = transactionsItemDetails[0]?.shiftId;

  const filteredTransactions = useMemo(() => {
    let filtered = [...paymentMethodTransactions];

    // Apply search filter
    if (searchKey) {
      const searchValue = searchKey.toLowerCase();

      filtered = filtered.filter((transaction) => {
        if (searchType === "transactionNo") {
          return transaction.transactionNumber?.toLowerCase().includes(searchValue);
        } else if (searchType === "refNumber") {
          return transaction.refNumber?.toLowerCase().includes(searchValue);
        }
        return false;
      });
    }

    return filtered;
  }, [paymentMethodTransactions, searchKey, searchType]);

  const paginatedTransactions = useMemo(() => {
    const { page, perPage } = pageState;
    const start = (page - 1) * perPage;
    const end = start + perPage;
    return filteredTransactions.slice(start, end);
  }, [filteredTransactions, pageState]);

  const { table, filter, search } = useDataTable({
    value: paginatedTransactions || [],
    filter: { initialState: initialFilterState(), mapper: (data) => data },
    table: {
      key: "nonCash",
      name: "nonCashTransactions",
      mapper: (val, index) => ({
        ...nonCashTransactionMapper({
          val,
          index,
          transactionsItemDetails,
          allPaymentMethodRows: paymentMethodTransactions,
          initialState,
          method,
          onRefetch,
          pageMode,
          updatedRefNumbers,
          setUpdatedRefNumbers,
          updatePaymentMethodRefNumber,
          editingRefId,
          setEditingRefId,
          onAdjustPaymentMethod: (transaction) => onAdjustPaymentMethod(transaction, index),
        }),
        ...(isAccountabilityView ? renderAccountabilityCells(val, index) : {}),
        isSelected:
          (adjustPaymentMethodDrawer.active || tagAccountabilityDrawer.active) &&
          index === adjustedRowIndex,
      }),
      columns,
    },
  });

  // Override filter and search with custom handlers
  const customFilter = {
    ...filter,
    filterState: {
      ...filter.filterState,
      searchType: searchType,
    },
    modifyFilter: (updates) => {
      if (updates.searchType !== undefined) {
        setSearchType(updates.searchType);
      }
      filter.modifyFilter(updates);
    },
  };

  const customSearch = {
    ...search,
    searchKey: searchKey,
    modifySearch: (value) => {
      setSearchKey(value);
    },
    applySearch: () => {},
    applyClearSearch: () => {
      setSearchKey("");
    },
  };

  useEffect(() => {
    setPageState(table.pageState);
  }, [table.pageState]);

  useEffect(() => {
    setSearchKey("");
    setSearchType("refNumber");
    setEditingRefId(null);
    setAdjustedTransaction(null);
    setAccountabilityTransaction(null);
    setAdjustedRowIndex(null);
    adjustPaymentMethodDrawer.close();
    tagAccountabilityDrawer.close();
    table.applyPage({ page: 1, perPage: 10 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal.active]);

  return (
    <Modal
      {...modal}
      title={prettifyPaymentMethod(method)}
      titleBlack
      headerExtra={<PaymentMethodTransactionFilter {...customFilter} {...customSearch} />}
      textHeaderStyle={styles.textHeader}
      headerStyle={styles.header}
      className={styles.modalBox}
    >
      {!isView && (
        <Highlight
          withIcon
          warning
          icon={<ReminderHexIcon />}
          className={styles.reminderHighlight}
          style={{
            opacity: 1,
            transform: "none",
            alignItems: "flex-start",
            paddingTop: 20,
            paddingBottom: 20,
            gap: 12,
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <Text
              color="var(--color-cheddar)"
              style={{ fontSize: 14, fontWeight: 900, lineHeight: "24px" }}
            >
              {locale.reminder}
            </Text>
            <Text color="var(--color-night)" style={{ fontSize: 14 }}>
              {locale.reminderToastMessageGiftCard}
            </Text>
          </div>
        </Highlight>
      )}
      <DataTableV2
        {...table}
        className={styles.tableContainer}
        result={{
          nonCash: paginatedTransactions,
        }}
        totalCount={filteredTransactions.length}
        refetch={() => {}}
        hideNoSearchResultRender
        tableVersion="v2"
        styleV2={true}
        size="small"
        childBottom={
          <Button onClick={() => modal?.close?.()} style={{ marginTop: "1.5em" }} outline>
            {locale.back}
          </Button>
        }
      />
      <AdjustPaymentMethodDrawer
        {...adjustPaymentMethodDrawer}
        transaction={adjustedTransaction}
        shiftId={shiftId}
        transactionsItemDetails={transactionsItemDetails}
        allPaymentMethodRows={paymentMethodTransactions}
        onUpdatePaymentMethod={(transactionPaymentMethodId, changes) => {
          setUpdatedRefNumbers((prev) => {
            if (!(transactionPaymentMethodId in prev)) return prev;
            if (changes?.method === PaymentMethod.Cash) {
              const { [transactionPaymentMethodId]: removed, ...rest } = prev;
              return rest;
            }
            return { ...prev, [transactionPaymentMethodId]: changes?.refNumber ?? "" };
          });
          updateTransactionPaymentMethod?.(transactionPaymentMethodId, changes);
        }}
      />
      <TagAccountabilityDrawer
        {...tagAccountabilityDrawer}
        transaction={accountabilityTransaction}
        shiftId={shiftId}
        employees={employees}
        onUpdateAccountability={updateTransactionAccountability}
      />
    </Modal>
  );
}
